import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { isAxiosError } from '../../../lib/api/axios';

import { ChipProps, ChipValueProps, useNotification } from 'spindl-shared-components';

import useGetAccount from '../../../modules/auth/useGetAccount';
import { Role } from '../../../modules/users/roles/types';
import { getRoleByIdOrSlug } from '../../../modules/users/roles/roles.repository';
import { UserAccount } from '../../blocks/UserAccountList/types';
import { displayUserInfo, isEmail, isEmptyString } from '../../../lib/utils';
import { NOTIFICATION_DURATION } from '../../../lib/constants';
import { ModalTypeEnum, UserListPageCombinedProps } from './types';
import { getAllRolesUseCase, sendResetPasswordEmailUseCase, updateAccountUseCase, updateRoleUseCase } from './UserListPage.interactor';
import { getTranslation } from '../../../lib/reactUtils';
import { isUser } from '../../blocks/UserAccountList/utils';
import { UpdateAccountPayload } from '../../../modules/users/types';
import { DUPLICATE_INSTANCES_CODE } from './constants';

const usePresenter = (props: UserListPageCombinedProps): UserListPageCombinedProps => {
  const { t } = useTranslation();
  const { account } = useGetAccount();
  const queryClient = useQueryClient();
  const { trigger } = useNotification();

  const [newRole, setNewRole] = useState<Role>();
  const [show, setShow] = useState<boolean>(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState('');
  const [modalType, setModalType] = useState<ModalTypeEnum>(ModalTypeEnum.ChangeRole);
  const [selectedAccount, setSelectedAccount] = useState<UserAccount>();
  const [chipProps, setChipProps] = useState<ChipProps & ChipValueProps>();
  const scrollContainerRef = useRef(null);

  const { data } = useQuery(['getAllRoles'], async () => getAllRolesUseCase());
  const { data : currentRole } = useQuery(['getRoleByIdOrSlug', selectedAccount], async () => {
    if (selectedAccount && selectedAccount.roleId) {
      return getRoleByIdOrSlug(`${selectedAccount.roleId}`);
    }
  });
  const { mutateAsync: updateRole } = useMutation('updateRole', async () => {
    if (selectedAccount && newRole) {
      await updateRoleUseCase(selectedAccount.id, { roleId: newRole.id } );
    }
  });
  const { mutateAsync: sendResetPasswordEmail } = useMutation('sendResetPasswordEmail', async () => {
    if (account && selectedAccount) {
      await sendResetPasswordEmailUseCase(selectedAccount.id);
    }
  });
  const { mutateAsync: updateAccount } = useMutation('updateAccount', async (payload: UpdateAccountPayload) => {
    if (account && selectedAccount) {
      await updateAccountUseCase(selectedAccount.id, payload);
    }
  });

  const handleCloseModal = () => {
    setNewRole(undefined);
    setSelectedAccount(undefined);
    setShow(false);
    setEmailValue('');
    setEmailError('');
  };

  const handleSaveRole = async () => {
    try {
      await updateRole();
      trigger({
        duration: NOTIFICATION_DURATION,
        message: getTranslation('toast_notification.update_role.success', {
          name: displayUserInfo(selectedAccount),
          role: newRole?.roleName,
        }),
        type: 'Positive',
      });
    } catch (e) {
      // no-op
    } finally {
      await queryClient.invalidateQueries('getUsers');
      handleCloseModal();
    }
  };

  const handleSendResetPasswordEmail = async () => {
    try {
      await sendResetPasswordEmail();
      trigger({
        duration: NOTIFICATION_DURATION,
        message: getTranslation('toast_notification.reset_password.success', {
          email: selectedAccount?.email,
        }),
        type: 'Positive',
      });
    } catch (e) {
      // no-op
    } finally {
      handleCloseModal();
    }
  };

  const handleDisableUser = async () => {
    try {
      await updateAccount({ enabled: false });
      trigger({
        duration: NOTIFICATION_DURATION,
        message: getTranslation('toast_notification.disable_user.success', {
          name: displayUserInfo(selectedAccount),
        }),
        type: 'Positive',
      });
    } catch (e) {
      // no-op
    } finally {
      await queryClient.invalidateQueries('getUsers');
      handleCloseModal();
    }
  };

  const handleEmailUpdate = async () => {
    try {
      const email = emailValue.trim().toLowerCase();
      if (!isEmail(email)) {
        setEmailError(t('modal.email_update.error.invalid_format'));
      } else if (selectedAccount?.email === email) {
        setEmailError(t('modal.email_update.error.already_exist'));
      } else {
        await updateAccount({ email: emailValue });
        await queryClient.invalidateQueries('getUsers');
        handleCloseModal();
        trigger({
          duration: NOTIFICATION_DURATION,
          message: getTranslation('toast_notification.email_update.success', {
            name: displayUserInfo(selectedAccount),
          }),
          type: 'Positive',
        });
      }
    } catch (e) {
      if (isAxiosError(e) && e?.response) {
        const { status } = e.response;
        if (status === DUPLICATE_INSTANCES_CODE) {
          setEmailError(t('modal.email_update.error.already_exist'));
        }
      }
    }
  };

  const renderRoleItems = () => {
    if (data) {
      return data.filter((role) => {
        if (role.id !== selectedAccount?.roleId) {
          return role;
        }
      }).map((differentRole) => {
        if (differentRole) {
          return {
            text: {
              value: differentRole.roleName,
            },
            onClick: () => { setNewRole(differentRole); },
          };
        }
      });
    }
  };

  const getResetPasswordDescription = () => {
    if (selectedAccount && isUser(selectedAccount)) {
      const fullName = `${selectedAccount.firstName} ${selectedAccount.lastName}`;
      const transKey = isEmptyString(fullName)
        ? 'modal.reset_password.description.without_name'
        : 'modal.reset_password.description.with_name';
      return getTranslation(transKey, {
        name: fullName,
        email: selectedAccount.email,
      });
    }
  };

  let modalProps;
  switch (modalType) {
    case ModalTypeEnum.ChangeRole:
      modalProps = {
        type: 'Update',
        styleType: 'Default',
        title: {
          value: t('modal.change_role.heading'),
        },
        button: {
          icon: {
            asset: 'Close',
          },
          onClick: handleCloseModal,
        },
        text: {
          value: 
          getTranslation('modal.change_role.description', {
            name: displayUserInfo(selectedAccount),
          }),
        },
        beforeLabel: {
          value: t('modal.change_role.label.before'),
        },
        afterLabel: {
          value: t('modal.change_role.label.after'),
        },
        actionButton: {
          text: {
            value: t('modal.change_role.button.action'),
          },
          disabled: newRole ? false : true,
          onClick: handleSaveRole,
        },
        cancelButton: {
          text: {
            value: t('modal.change_role.button.cancel'),
          },
          onClick: handleCloseModal,
        },
        textContainer: {
          text: {
            value: currentRole ? currentRole.roleName : t('default.role'),
          },
        },
        textDropDown: {
          toggleProps: {
            name: {
              value: newRole ? newRole.roleName : t('modal.change_role.label.role'),
            },
            icon: {
              asset: 'ChevronDown',
            },
          },
          menuProps: {
            dropDownItems: renderRoleItems(),
          },
        },
      };
      break;

    case ModalTypeEnum.ResetPassword:
      modalProps = {
        type: 'Default',
        styleType: 'Default',
        title: {
          value: t('modal.reset_password.heading'),
        },
        button: {
          icon: {
            asset: 'Close',
          },
          onClick: handleCloseModal,
        },
        text: {
          value: getResetPasswordDescription(),
        },
        actionButton: {
          text: {
            value: t('modal.reset_password.button.action'),
          },
          onClick: handleSendResetPasswordEmail,
        },
        cancelButton: {
          text: {
            value: t('modal.change_role.button.cancel'),
          },
          onClick: handleCloseModal,
        },
      };
      break;

    case ModalTypeEnum.DisableUser:
      modalProps = {
        type: 'DisableUser',
        styleType: 'Destructive',
        title: {
          value: t('modal.disable_user.heading'),
        },
        button: {
          icon: {
            asset: 'Close',
          },
          onClick: handleCloseModal,
        },
        border: {
        },
        text: {
          value: getTranslation('modal.disable_user.description', {
            name: displayUserInfo(selectedAccount),
          }),
        },
        userSummary: {
          name: {
            targetProps: {
              type: 'Text',
              state: 'Default',
              size: 'FullWidth',
              text: {
                value: displayUserInfo(selectedAccount),
              },
            },
          },
          email: {
            targetProps: {
              type: 'Text',
              state: 'Default',
              size: 'FullWidth',
              text: {
                value: selectedAccount?.email,
              },
            },
          },
          role: {
            targetProps: {
              type: 'Chip',
              state: 'Default',
              size: 'S',
              chip: chipProps,
            },
          },
        },
        caption: {
          value: getTranslation('modal.disable_user.caption'),
        },
        actionButton: {
          text: {
            value: t('modal.disable_user.button.action'),
          },
          onClick: handleDisableUser,
        },
        cancelButton: {
          text: {
            value: t('modal.disable_user.button.cancel'),
          },
          onClick: handleCloseModal,
        },
      };
      break;

    case ModalTypeEnum.EmailUpdate:
      modalProps = {
        type: 'EmailUpdate',
        styleType: 'Default',
        title: {
          value: t('modal.email_update.heading'),
        },
        button: {
          icon: {
            asset: 'Close',
          },
          onClick: handleCloseModal,
        },
        text: {
          value: getTranslation('modal.email_update.description', {
            name: displayUserInfo(selectedAccount),
          }),
        },
        beforeLabel: {
          value: t('modal.email_update.before_label'),
        },
        textContainer: {
          text: {
            value: selectedAccount?.email,
          },
        },
        icon: {
          asset: 'ArrowDown',
        },
        textField: {
          label: {
            value: t('modal.email_update.text_field_label'),
          },
          textInput: {
            onTextChanged: (value: string) => {
              setEmailValue(value);
              if (emailError) {
                setEmailError('');
              }
            },
          },
          error: emailError ? 'Error' : 'None',
          text: {
            value: emailError,
          },
        },
        actionButton: {
          text: {
            value: t('modal.email_update.button.action'),
          },
          onClick: handleEmailUpdate,
          disabled: !emailValue,
        },
        cancelButton: {
          text: {
            value: t('modal.email_update.button.cancel'),
          },
          onClick: handleCloseModal,
        },
      };
      break;
  }

  return {
    ...props,
    scrollContainerRef,
    buttonModal: {
      show: show,
      modalProps: modalProps,
    },
    setShow: setShow,
    setModalType: setModalType,
    setSelectedAccount: setSelectedAccount,
    setChipProps: setChipProps,
  };
};

export default usePresenter;
